// SpaceNewsDashboard.tsx
// import React from 'react';
import React, { useState, useEffect, useRef } from 'react';
import './index.scss';
import { Modal, Image } from 'antd';
import {
	FilePdfFilled,
	FilePptFilled,
	FileTextFilled,
	VideoCameraFilled,
	LayoutFilled,
	CloseOutlined, 
  } from '@ant-design/icons';
import _ from 'lodash';
import NeurIPS2026Logo from '../../../assets/images/3-data-art/NeurIPS-ai-dance-2026/NeurIPS_logo.png';


interface ArtworkCardProps {
	data: any;
	textIdx: number;
}

const ArtworkCard: React.FC<ArtworkCardProps> = ({ data, textIdx }) => {


//   console.log('data', data);

  // find video
  const videoItem = data.suppMaterialList.find((item: any) => item.name === 'Demo Video')


  const fadeInElements = useRef<NodeListOf<Element> | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => {
    console.log('showModal');
    setIsModalOpen(true);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // the exhibition text: does more of it sit below the fold of its tile?
  const textRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(false);
  const checkMore = () => {
    const el = textRef.current;
    if (el) setHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    checkMore();
    // measured on the next frame, so the hint's own update can't feed back into the observer in the same frame
    let raf = 0;
    const ro = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(checkMore); });
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    fadeInElements.current = document.querySelectorAll('.fade-in');

    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target); // Stop observing once the element is visible
          }
        });
      },
      { threshold: 0.1 }
    );

    fadeInElements.current.forEach((el) => observer.observe(el));

    return () => {
      fadeInElements.current?.forEach((el) => observer.unobserve(el));
    };
  }, []);


  const authorElementList: JSX.Element[] = []
  _.forEach(data.authorList, (author, idx: number) => {
    if (author === 'Yifang Wang') {
      authorElementList.push(<span className='author-highlight' key={`${textIdx}-${idx}-${author}-highlight`} id={`${textIdx}-${idx}-${author}`}>{author}</span>)
    } else {
      authorElementList.push(<span key={`${textIdx}-${idx}-${author}-common`} id={`${textIdx}-${idx}-${author}`}>{author}</span>)
    }
    if (idx < data.authorList.length - 1) {
      authorElementList.push(<span key={`${textIdx}-${idx}-${author}-comma`} id={`${textIdx}-${idx}-${author}`}>, </span>)
    }
  })

  return (
    // one artwork, set as an editorial gallery spread (black ground): quoted title and meta, the teaser offset to the right,
    // the design idea beside its image, three captioned tiles, and a running "view the full project" strip
    <div className="artwork-card-div aw">
		<header className="aw-head fade-in">
			<h3 className="aw-title">“{data.title}”</h3>
			<div className="aw-meta">
				<span>{data.venueShort}</span>
				<span>{data.year}</span>
				<span className="aw-arrow" aria-hidden="true">↓</span>
			</div>
		</header>
		<div className="aw-hero fade-in">
			<img src={data.teaser} alt={data.title} />
		</div>

		<section className="aw-split fade-in">
			<div className="aw-split-img">
				<img src={data.detailPageData.secondaryImageInfo.image} alt={data.detailPageData.secondaryImageInfo.title} />
			</div>
			<div className="aw-split-text">
				<h4 className="aw-h">{data.detailPageData.secondaryImageInfo.title}</h4>
				<div className="aw-row">
					<span className="aw-label">Design idea</span>
					<div className="aw-body">{data.detailPageData.secondaryImageInfo.description}</div>
				</div>
			</div>
		</section>

		<section className="aw-tiles fade-in">
			<figure className="aw-tile">
				<figcaption className="aw-tile-title">{data.detailPageData.exihibitionImageInfo.title}</figcaption>
				<div className="aw-tile-media">
					{videoItem ? (
						<video controls autoPlay={true} muted>
							<source src={videoItem.fileLink} type="video/mp4" />
							Your browser does not support the video tag.
						</video>
					) : (
						<img src={data.detailPageData.exihibitionImageInfo.image} alt="exhibition" />
					)}
				</div>
				<p className="aw-tile-meta">{data.detailPageData.exihibitionImageInfo.time}</p>
				<p className="aw-tile-sub">{videoItem ? 'Demo video' : 'Exhibition'}</p>
			</figure>
			<figure className="aw-tile">
				<figcaption className="aw-tile-title">Exhibition &amp; award</figcaption>
				<div className="aw-tile-media">
					<img src={data.detailPageData.awardImageInfo.image} alt="exhibition and award" />
				</div>
				<div className="aw-logos">
					{data.detailPageData.awardImageInfo.awardLogoList.map((image: string, index: number) => (
						<img key={index} className={image === NeurIPS2026Logo ? 'white-logo' : undefined} src={image} alt="Award" />
					))}
				</div>
			</figure>
			{/* as tall as the two tiles beside it: longer text scrolls inside, with a hint while more is hidden */}
			<div className="aw-tile aw-tile-text">
				<div className="aw-tile-scroll" ref={textRef} onScroll={checkMore}>
					<span className="aw-label">Exhibition</span>
					<div className="aw-body">{data.detailPageData.exihibitionImageInfo.description}</div>
					<button type="button" className="aw-link" onClick={showModal}>Read more</button>
				</div>
				<div className={`aw-scroll-hint${hasMore ? ' on' : ''}`} aria-hidden="true">Scroll <span>↓</span></div>
			</div>
		</section>

		<button type="button" className="aw-marquee" onClick={showModal} aria-label={`View the full project: ${data.title}`}>
			<span className="aw-marquee-track" aria-hidden="true">
				{Array.from({ length: 8 }, (_, i) => <span key={i}>View the full project · {data.titleShort || data.title} · </span>)}
			</span>
			<span className="aw-marquee-btn"><span aria-hidden="true">◉</span> View the full project</span>
		</button>


		<Modal
	        key={data.title}
	        className="artwork-modal"
	        // title={item.titleShort}
	        open={isModalOpen}
	        onCancel={handleCancel}
	        closeIcon={<CloseOutlined onClick={handleCancel} />}
	        footer={null}  // Remove default footer buttons
	        width={'80%'}
	        centered
	        // style={{ top: 20 }}  // Adjust top margin if needed
	      >
	        <div className='section' style={{margin: '0 0'}}>
	          {videoItem ? (
	            <video controls autoPlay={true} muted style={{width: '100%', height: '420px', objectFit: 'cover'}}>
	              <source src={videoItem.fileLink} type="video/mp4" />
	              Your browser does not support the video tag.
	            </video>
	          ) : (
	            <figure style={{width: '100%', height: '420px', margin: 0}}>
	              <img src={data.detailPageData.exihibitionImageInfo.image} alt="exhibition" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
	            </figure>
	          )}
	        </div>
	        {/* <div className='section' style={{margin: '0 0', position: 'relative'}}>
	          {
	            (videoItem) && 
	            <video controls autoPlay={true} muted style={{position: 'absolute', width: '100%', height: '100%', objectFit: 'cover'}}>
	              <source src={videoItem.fileLink} type="video/mp4" />
	              Your browser does not support the video tag.
	            </video>
	          }
	          <div className='artwork-title' style={{position: 'relative', zIndex: 1}}>
	            <div>{data.title}</div>
	          </div>
	        </div> */}
	        <div className='section'>
	          <div className='section-title'>Publication</div>
	          <div className='title'>{data.title}</div>
	          <div className='author'>{authorElementList}</div>
	          <div className='supp'>
	            [{data.suppMaterialList.map((suppItem: any, idx: number) => {
	                let hrefElement = <></>
	                if (idx === 0) {
	                  if (data.suppMaterialList.length > 1) {
	                    hrefElement = <a href={suppItem.fileLink}>{suppItem.name} |</a>
	                  } else {
	                    hrefElement = <a href={suppItem.fileLink}>{suppItem.name}</a>
	                  }
	                }
	                else if (idx < data.suppMaterialList.length - 1) {
	                  hrefElement = <a href={suppItem.fileLink}> {suppItem.name} |</a>
	                } else {
	                  hrefElement = <a href={suppItem.fileLink}> {suppItem.name}</a>
	                }

	                let iconElement = <></>
	                if (suppItem.name === 'PDF') {
	                  iconElement = <FilePdfFilled />
	                  // iconElement = <img src={pdfIconUrl} alt='pdf-icon'/>
	                } else if (suppItem.name === 'Demo Video' || suppItem.name === 'Background Video' || suppItem.name === 'Case Video') {
	                  iconElement = <VideoCameraFilled />
	                  // iconElement = <img src={videoIconUrl} alt='video-icon'/>
	                } else if (suppItem.name === 'Slides') {
	                  iconElement = <FilePptFilled />
	                  // iconElement = <img src={pptIconUrl} alt='ppt-icon'/>
	                } else if (suppItem.name === 'Project Page' || suppItem.name === 'Official Website') {
	                  iconElement = <LayoutFilled />
	                  // iconElement = <img src={websiteIconUrl} alt='website-icon'/>
	                } else if (suppItem.name === 'Supp Material' || suppItem.name === 'ICSSI2023 Poster') {
	                  iconElement = <FileTextFilled />
	                  // iconElement = <img src={websiteIconUrl} alt='website-icon'/>
	                }

	                return (<div className='supp-unit-div'>
	                  {iconElement}
	                  {hrefElement}
	                </div>)
	              })}]
	          </div>
	        </div>
	        {
	          (data.award.length > 0) && 
	          <div className='section'>
	            <div className='section-title'>Exhibition & Prize</div>
	            <div className='award' style={{marginTop: 8}}>
	              { (data.award.map((item: string) => 
	                <div className='content'>
	                  {item}
	                </div>
	              ))}
	            </div>
	          </div>
	        }
	        { (data.mediaCoverage.length > 0) && 
	          <div className='section'>
	            <div className='section-title'>Media Coverage</div>
	            { (data.mediaCoverage.map((item: { name: string, title: string, fileLink: string }) => 
	            <div className='media-unit'>
	                <div className='media-unit-section media-title'>
	                  <div className='field-wrapper'>
	                    <div className='field'>Title</div>
	                  </div>
	                  <div className='content'><a href={item.fileLink}>{item.title}</a></div>
	                </div>
	                <div className='media-unit-section media-name'>
	                  <div className='field-wrapper'>
	                    <div className='field'>Media</div>
	                  </div>
	                  <div className='content'>{item.name}</div>
	                </div>
	            </div>))}
	          </div>
	        }
	        {
	          (data.imageList.length > 0) && 
	          <div className='section'>
	            <div className='section-title'>Gallery</div>
	            <div className='gallery' style={{marginTop: 8}}>
	              {data.imageList.map((image: string, index: number) => { 
	                const fileExtension = image.slice(image.length - 3, image.length).toLowerCase();
	                if (!['mp4'].includes(fileExtension)) {
	                  return <Image alt={`${index}`} src={image} />
	                } else {
	                  return  <video controls autoPlay={false} muted>
	                            <source src={image} type="video/mp4" />
	                            Your browser does not support the video tag.
	                          </video>
	                }
	              })}
	            </div>
	            {/* https://github.com/neptunian/react-photo-gallery and https://benhowell.github.io/react-grid-gallery */}
	          </div>
	        }
	        <div className='section'>
	          <div className='section-title'>Description</div>
	          <div style={{marginTop: 16}} className='artwork-story'>{data.introduction}</div>
	        </div>
	        <div className='section'>
	          <div className='section-title'>Story</div>
	          <div style={{marginTop: 16}} className='artwork-story'>{data.story}</div>
	        </div>
	    </Modal>
  </div>
  );
};

export default ArtworkCard;
